import { type FC, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  CloseIcon,
  Column,
  Combobox,
  GroupedCombobox,
  HiddenFormLabel,
  IconButton,
  RefreshIcon,
  Select,
  Text,
  TextInput,
  Tooltip,
  TraitIcon,
  HtImage,
} from "@hightouch/htui";
import { isPresent } from "ts-extras";

import { useDestinationForm } from "src/contexts/destination-form-context";
import { usePermissionContext } from "src/components/permission/permission-context";
import { NEW_ICON_MAP } from "src/utils/destinations";

import { useQuery } from "src/formkit/hooks";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import {
  type ExtendedOption,
  type FormkitGraphQLReference,
  toExtendedOption,
} from "@hightouch/formkit";
import type { ExtendedAssociationOption } from "@hightouch/formkit/src/api/components/association-option";
import { Mapper } from "./mapper";
import {
  MappingType,
  type AdvancedColumn,
  type JsonColumnProps,
} from "src/formkit/components/types";
import { ADVANCED_ASSOCIATION_MAPPER_DESTINATION_TYPES } from "./utils";

type LookupCondition = {
  by: string | undefined;
  byType: string | undefined;
  value: unknown;
};

type Lookup = {
  by: string | undefined;
  byType: string | undefined;
  from: string | AdvancedColumn; //supporting advanced columns with nested properties
  object: string;
  conditions?: LookupCondition[];
};

type Value = {
  lookup: Lookup;
  to: string;
  type: "reference";
};

export type AssociationOptions =
  | ExtendedAssociationOption
  | FormkitGraphQLReference;

type Props = {
  name: string;
  // Static association options or handler reference to fetch props base on asc object
  associationOptions?: AssociationOptions;
  associationObjectLabels?: Record<string, unknown>;
  index: number;
  onChange?: (value: Value) => void;
  value: Value;
  isDisabled?: boolean;
  // Declared by the destination's MappingsSection (`allowLookupConditions`);
  // only destinations whose lookup resolution applies `lookup.conditions`
  // should enable this.
  allowLookupConditions?: boolean;
};

export const AssociationMapper: FC<Readonly<Props>> = ({
  allowLookupConditions = false,
  associationOptions,
  index,
  name,
  onChange,
  value,
  isDisabled = false,
}) => {
  const {
    columns,
    destination,
    destinationDefinition,
    model,
    sourceDefinition,
  } = useFormkitContext();

  // FE feature flags do not have access to the destination type.
  // Do not enable this for destinations that use the associations framework.
  const useAdvancedAssociationMapper =
    ADVANCED_ASSOCIATION_MAPPER_DESTINATION_TYPES.includes(
      destination?.type || "",
    );

  const [jsonColumnProperties, setJsonColumnProperties] =
    useState<JsonColumnProps>({
      selectedColumnProps: undefined,
      allColumnsProps: undefined,
    });

  const { errors } = useDestinationForm();
  const asyncAscOptions =
    associationOptions?.["query"] &&
    typeof associationOptions?.["query"] === "string" &&
    isPresent(associationOptions);

  const variables = {
    ...associationOptions?.variables?.["input"]?.variables,
    ...{ object: value?.lookup?.object ?? value?.to },
  };

  const useAsyncOptions = Boolean(
    asyncAscOptions && (value?.lookup?.object ?? value?.to),
  );

  const {
    data: associatedObjectFields,
    isFetching,
    refetch,
  } = useQuery(
    JSON.stringify({
      name,
      variables: {
        ...associationOptions?.variables,
        input: { ...associationOptions?.variables?.["input"], variables },
      },
    }),
    {
      enabled: useAsyncOptions,
      fetchProps: {
        destinationId: destination?.id,
        modelId: model?.id,
        query: associationOptions?.["query"],
        variables: {
          ...associationOptions?.variables,
          input: { ...associationOptions?.variables?.["input"], variables },
        },
      },
    },
  );

  // Returning an `Option[]` from the handler should be considered deprecated,
  // this component now supports `FormkitAssociationOptionsHandler` which will allow
  // you to return `AssociationOption`.
  const ascFields: ExtendedOption[] = toExtendedOption(
    asyncAscOptions
      ? Array.isArray(associatedObjectFields)
        ? associatedObjectFields
        : associatedObjectFields?.[value.lookup.object]
      : associationOptions?.[value.lookup.object],
  );

  const permission = usePermissionContext();

  // Items in `columns` have `options` field as optional,
  // but `GroupedCombobox` expects it to be required
  const columnOptionGroups = useMemo(() => {
    return (columns ?? []).map((group) => ({
      ...group,
      options: group.options ?? [],
    }));
  }, [columns]);

  const conditions = value?.lookup?.conditions ?? [];

  const setConditions = (next: LookupCondition[]) => {
    onChange?.({
      ...value,
      lookup: {
        ...value?.lookup,
        conditions: next.length ? next : undefined,
      },
    });
  };

  const fieldOptionAccessories = (option: ExtendedOption) => {
    const type = option?.extendedType?.type;

    return type
      ? {
          leftAccessory: {
            type: "icon" as const,
            icon: NEW_ICON_MAP[type],
          },
        }
      : undefined;
  };

  const fieldOptionLabel = (option: ExtendedOption) => {
    return option.object?.label
      ? `${option.label} (${option.object.label})`
      : option.label;
  };

  return (
    <Box aria-label={`${name} association mapper`}>
      <Column gap={2}>
        <Box
          alignItems="center"
          display="grid"
          gap={2}
          gridTemplateColumns="56px max-content"
        >
          <Text>Find</Text>
          <Badge>{value?.lookup?.object ?? value?.to}</Badge>
        </Box>

        <Box
          alignItems="center"
          display="grid"
          gap={2}
          gridTemplateColumns="56px 16px minmax(0, 1fr) 24px"
        >
          <Text>where</Text>

          {destinationDefinition?.icon && (
            <HtImage
              src={destinationDefinition.icon}
              width="4"
              alt={destinationDefinition.name}
            />
          )}

          <HiddenFormLabel label="Select a field">
            <Combobox
              isDisabled={isDisabled || permission.unauthorized}
              isInvalid={Boolean(errors?.[`${name}[${index}].lookup.by`])}
              optionAccessories={fieldOptionAccessories}
              optionLabel={fieldOptionLabel}
              options={ascFields ?? []}
              placeholder={value?.lookup?.by ? "" : "Select a field"}
              value={value?.lookup?.by}
              width="100%"
              onChange={(newValue) => {
                const option = ascFields.find(
                  (option) => option.value === newValue,
                );

                onChange?.({
                  to: value.to,
                  type: value.type,
                  lookup: {
                    ...value?.lookup,
                    by: newValue,
                    byType: option?.extendedType?.type,
                  },
                });
              }}
            />
          </HiddenFormLabel>

          {useAsyncOptions && (
            <Tooltip message="Refresh fields">
              <IconButton
                aria-label="Refresh fields"
                isDisabled={isDisabled || permission.unauthorized}
                icon={RefreshIcon}
                isLoading={isFetching}
                size="sm"
                variant="secondary"
                onClick={() => {
                  void refetch();
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box
          alignItems="center"
          display="grid"
          gap={2}
          gridTemplateColumns="56px 16px minmax(0, 1fr)"
        >
          <Text>equals</Text>

          {sourceDefinition?.icon && (
            <HtImage
              src={sourceDefinition.icon}
              width="4"
              alt={sourceDefinition.name}
            />
          )}

          {useAdvancedAssociationMapper ? (
            <Mapper
              enableInLineMapper={false}
              isDisabled={isDisabled || permission.unauthorized}
              isError={Boolean(errors?.[`${name}[${index}].lookup.from`])}
              jsonColumnProperties={jsonColumnProperties}
              placeholder={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
              selectedOption={undefined} // not needed because the to side is not going to change our values
              templates={[]}
              value={
                typeof value.lookup.from === "object"
                  ? (value?.lookup?.from ?? undefined)
                  : (value?.lookup ?? undefined)
              }
              onReloadEligibleInlineMapperColumns={() => {}}
              //If the mapping type is a column (standard), use the from property with type string
              //If the mapping type is static, variable, or template, use the from property with type object
              onChange={(newMapping) => {
                if (newMapping.type === MappingType.STANDARD) {
                  onChange?.({
                    ...value,
                    lookup: {
                      ...value.lookup,
                      from: newMapping.from,
                    },
                  });
                } else {
                  onChange?.({
                    ...value,
                    lookup: {
                      ...value.lookup,
                      from: newMapping,
                    },
                  });
                }
              }}
              onChangeJsonColumnProperties={setJsonColumnProperties}
            />
          ) : (
            <HiddenFormLabel label="Select a column">
              <GroupedCombobox
                isDisabled={isDisabled || permission.unauthorized}
                isInvalid={Boolean(errors?.[`${name}[${index}].lookup.from`])}
                optionAccessories={(option) => {
                  return typeof option?.value === "object" &&
                    option?.value?.type === "additionalColumnReference"
                    ? {
                        leftAccessory: { type: "icon", icon: TraitIcon },
                      }
                    : undefined;
                }}
                optionGroups={columnOptionGroups}
                placeholder={value?.lookup?.from ? "" : "Select a column"}
                value={value?.lookup?.from}
                width="100%"
                onChange={(newValue) => {
                  onChange?.({
                    ...value,
                    lookup: {
                      ...value?.lookup,
                      from: newValue ?? "",
                    },
                  });
                }}
              />
            </HiddenFormLabel>
          )}
        </Box>

        {allowLookupConditions &&
          conditions.map((condition, conditionIndex) => (
            <Box
              key={conditionIndex}
              alignItems="center"
              display="grid"
              gap={2}
              gridTemplateColumns="56px 16px minmax(0, 1fr) max-content minmax(0, 1fr) 24px"
            >
              <Text>and</Text>

              {destinationDefinition?.icon && (
                <HtImage
                  src={destinationDefinition.icon}
                  width="4"
                  alt={destinationDefinition.name}
                />
              )}

              <HiddenFormLabel label="Select a condition field">
                <Combobox
                  isDisabled={isDisabled || permission.unauthorized}
                  isInvalid={Boolean(
                    errors?.[
                      `${name}[${index}].lookup.conditions[${conditionIndex}].by`
                    ],
                  )}
                  optionAccessories={fieldOptionAccessories}
                  optionLabel={fieldOptionLabel}
                  options={ascFields ?? []}
                  placeholder={condition.by ? "" : "Select a field"}
                  value={condition.by}
                  width="100%"
                  onChange={(newValue) => {
                    const option = ascFields.find(
                      (option) => option.value === newValue,
                    );
                    const byType = option?.extendedType?.type;

                    setConditions(
                      conditions.map((current, i) =>
                        i === conditionIndex
                          ? {
                              by: newValue,
                              byType,
                              // A different field type invalidates the value
                              value:
                                byType === current.byType
                                  ? current.value
                                  : undefined,
                            }
                          : current,
                      ),
                    );
                  }}
                />
              </HiddenFormLabel>

              <Text>equals</Text>

              {condition.byType === "BOOLEAN" ? (
                <HiddenFormLabel label="Select a value">
                  <Select
                    isDisabled={isDisabled || permission.unauthorized}
                    isInvalid={Boolean(
                      errors?.[
                        `${name}[${index}].lookup.conditions[${conditionIndex}].value`
                      ],
                    )}
                    options={[
                      { label: "True", value: true },
                      { label: "False", value: false },
                    ]}
                    placeholder="Select a value"
                    value={
                      typeof condition.value === "boolean"
                        ? condition.value
                        : undefined
                    }
                    width="100%"
                    onChange={(newValue) => {
                      setConditions(
                        conditions.map((current, i) =>
                          i === conditionIndex
                            ? { ...current, value: newValue }
                            : current,
                        ),
                      );
                    }}
                  />
                </HiddenFormLabel>
              ) : (
                <HiddenFormLabel label="Enter a value">
                  <TextInput
                    isDisabled={isDisabled || permission.unauthorized}
                    isInvalid={Boolean(
                      errors?.[
                        `${name}[${index}].lookup.conditions[${conditionIndex}].value`
                      ],
                    )}
                    placeholder={
                      condition.byType === "NUMBER"
                        ? "Enter a number"
                        : condition.byType === "DATE"
                          ? "e.g. 2026-01-01"
                          : condition.byType === "DATETIME"
                            ? "e.g. 2026-01-01T00:00:00Z"
                            : "Enter a value"
                    }
                    type={condition.byType === "NUMBER" ? "number" : "text"}
                    value={
                      condition.value == null ? "" : String(condition.value)
                    }
                    width="100%"
                    onChange={(event) => {
                      setConditions(
                        conditions.map((current, i) =>
                          i === conditionIndex
                            ? { ...current, value: event.target.value }
                            : current,
                        ),
                      );
                    }}
                  />
                </HiddenFormLabel>
              )}

              <Tooltip message="Remove condition">
                <IconButton
                  aria-label="Remove condition"
                  icon={CloseIcon}
                  isDisabled={isDisabled || permission.unauthorized}
                  onClick={() => {
                    setConditions(
                      conditions.filter((_, i) => i !== conditionIndex),
                    );
                  }}
                />
              </Tooltip>
            </Box>
          ))}

        {allowLookupConditions && (
          <Box>
            <Button
              isDisabled={isDisabled || permission.unauthorized}
              size="sm"
              onClick={() => {
                setConditions([
                  ...conditions,
                  { by: undefined, byType: undefined, value: undefined },
                ]);
              }}
            >
              Add condition
            </Button>
          </Box>
        )}
      </Column>
    </Box>
  );
};
